import {useRouteLoaderData} from '@github-ui/react-router'

import type {RouteQueryKey} from '../query-key'
import type {AnyQueryRoute, RouteMatchData} from './query-route'

/**
 * Returns the {@link RouteQueryKey} for a named query on the given route, or
 * `undefined` if the route is not currently mounted.
 *
 * Prefer this over reaching into `loaderData.queries[name].queryConfig.queryKey`
 * directly. The full `queryConfig` shape on loader data is implementation
 * detail and will eventually be removed in favor of just `queryKey`.
 *
 * Use cases:
 * - Looking up cached data via `queryClient.getQueryData(queryKey)` from a
 *   component that lives above (or beside) the owning route, where calling
 *   `useRouteQuery` isn't appropriate.
 */
export function useRouteQueryKey<Config extends AnyQueryRoute, QueryName extends string & keyof Config['queries']>(
  route: Config,
  queryName: QueryName,
): RouteQueryKey | undefined {
  const data = useRouteLoaderData<RouteMatchData<Config> | undefined>(route.id)
  return data?.queries?.[queryName]?.queryKey
}
